import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { extractExploreUnknownRewardPopupsFromWinRewards } from './extractExploreUnknownRewardPopupsFromWinRewards';
import ExploreUnknownDarkKnowledgeRewardModal from './ExploreUnknownDarkKnowledgeRewardModal';
import ExploreUnknownHeartSetCompleteModal from './ExploreUnknownHeartSetCompleteModal';
import ExploreUnknownEpSetCompleteModal from './ExploreUnknownEpSetCompleteModal';
import ExploreUnknownRewriteSetCompleteModal from './ExploreUnknownRewriteSetCompleteModal';
import './ExploreUnknownRewardPopups.css';

/**
 * Shows battle-parity fragment-set reward dialogs after a minigame win, one at a time.
 *
 * @param {boolean} active
 * @param {Array<{ id: string }> | null} winRewards
 * @param {{ id: string } | null} winReward
 * @param {(blocking: boolean) => void} onBlockingChange
 */
export default function ExploreUnknownMinigameRewardPopupQueue({
  active,
  winRewards,
  winReward,
  onBlockingChange,
}) {
  const [popupIndex, setPopupIndex] = useState(0);

  const popups = useMemo(() => {
    if (!active) {
      return [];
    }
    return extractExploreUnknownRewardPopupsFromWinRewards(winRewards, winReward);
  }, [active, winRewards, winReward]);

  useEffect(() => {
    setPopupIndex(0);
  }, [active, popups]);

  const currentPopup = popups[popupIndex] ?? null;
  const blocking = active && currentPopup != null;

  useEffect(() => {
    if (typeof onBlockingChange === 'function') {
      onBlockingChange(blocking);
    }
  }, [blocking, onBlockingChange]);

  const dismissCurrent = useCallback(() => {
    setPopupIndex((idx) => idx + 1);
  }, []);

  if (!currentPopup) {
    return null;
  }

  if (currentPopup.type === 'darkKnowledge') {
    return (
      <ExploreUnknownDarkKnowledgeRewardModal
        popupId={currentPopup.popupId}
        rewardType={currentPopup.rewardType}
        orderId={currentPopup.orderId}
        amount={currentPopup.amount}
        onDismiss={dismissCurrent}
      />
    );
  }

  if (currentPopup.type === 'heart') {
    return (
      <ExploreUnknownHeartSetCompleteModal
        popupId={currentPopup.popupId}
        variant={currentPopup.variant}
        i18nKey={currentPopup.i18nKey}
        imageSrc={currentPopup.imageSrc}
        healthBonus={currentPopup.healthBonus}
        onDismiss={dismissCurrent}
      />
    );
  }

  if (currentPopup.type === 'ep') {
    return (
      <ExploreUnknownEpSetCompleteModal
        popupId={currentPopup.popupId}
        variant={currentPopup.variant}
        i18nKey={currentPopup.i18nKey}
        imageSrc={currentPopup.imageSrc}
        newCharges={currentPopup.newCharges}
        onDismiss={dismissCurrent}
      />
    );
  }

  if (currentPopup.type === 'rewrite') {
    return (
      <ExploreUnknownRewriteSetCompleteModal
        popupId={currentPopup.popupId}
        newCharges={currentPopup.newCharges}
        onDismiss={dismissCurrent}
      />
    );
  }

  throw new Error(
    `ExploreUnknownMinigameRewardPopupQueue: unsupported popup type ${String(currentPopup.type)}`
  );
}
